import type { BlockParts } from './read-block-parts'

/**
 * One thing a creator can do in a block, assembled from the three files that
 * each hold a third of the answer: the element they press, the event it
 * raises, and the flow that runs.
 */
export interface BlockAction {
  /** The element's id — the name the three files join on. */
  elementId: string
  /** `button`, `input`, … from the ui tree. */
  elementType: string | null
  /** What the block calls this element. Shown in preference to the id. */
  semanticRole: string | null
  /** `tap`, `change`, … the half of the binding key after the colon. */
  event: string
  /** The flow this binding names, or null when it names none. */
  flow: string | null
  /**
   * Whether that flow is actually defined in this block.
   *
   * A binding naming a flow the block does not define is invisible in every
   * file taken alone — the binding looks complete and the flow file looks
   * complete. It is only visible in the join, which is why it is computed here
   * and not left to the reader to notice.
   */
  flowExists: boolean
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : undefined
}

function asString(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

/**
 * The names a block's flows go by, in either shape real data uses.
 *
 * Measured: `public/apps/newsletter-membership/blocks/*​/flow.json` keys its
 * flows by name (`{ flows: { joinMembership: … } }`), while
 * `public/agentic/sipass-reference/sipass-admin.cogo-app.json` carries a list
 * of flow objects (`[{ id, name, trigger, … }]`). Reading only the first
 * reported 554 of 554 bindings on that seed as running nothing — a screen-wide
 * false alarm telling the creator every button in their app is dead.
 *
 * Both `id` and `name` are admitted because the seed sets them to the same
 * string and a binding may reasonably reference either.
 */
function declaredFlowNames(flow: unknown): Set<string> {
  const names = new Set<string>()

  const addFromList = (list: unknown[]): void => {
    for (const entry of list) {
      const record = asRecord(entry)
      if (!record) continue
      for (const key of ['id', 'name'] as const) {
        const value = asString(record[key])
        if (value !== null) names.add(value)
      }
    }
  }

  if (Array.isArray(flow)) {
    addFromList(flow)
    return names
  }

  const record = asRecord(flow)
  if (!record) return names

  const inner = 'flows' in record ? record.flows : record
  if (Array.isArray(inner)) addFromList(inner)
  else if (asRecord(inner)) for (const key of Object.keys(inner as object)) names.add(key)

  return names
}

/** Every node in the ui tree that carries an id, by id. */
function indexUiElements(root: unknown): Map<string, Record<string, unknown>> {
  const found = new Map<string, Record<string, unknown>>()
  const walk = (node: unknown): void => {
    const record = asRecord(node)
    if (record) {
      const id = asString(record.id)
      if (id !== null && !found.has(id)) found.set(id, record)
      for (const value of Object.values(record)) walk(value)
      return
    }
    if (Array.isArray(node)) for (const item of node) walk(item)
  }
  walk(root)
  return found
}

/**
 * What happens when the creator presses each thing in this block.
 *
 * Driven by the bindings, because the binding is what declares that an
 * interaction exists at all — a button with no binding does nothing, and a
 * flow nobody binds to runs on nothing.
 */
export function describeBlockActions(parts: BlockParts): BlockAction[] {
  const bindings = asRecord(asRecord(parts.events)?.bindings)
  if (!bindings) return []

  const elements = indexUiElements(asRecord(parts.ui)?.root)
  const flows = declaredFlowNames(parts.flow)

  const actions: BlockAction[] = []
  for (const [key, value] of Object.entries(bindings)) {
    const separator = key.lastIndexOf(':')
    if (separator <= 0) continue
    const elementId = key.slice(0, separator)
    const event = key.slice(separator + 1)
    const element = elements.get(elementId)
    const flow = asString(asRecord(value)?.flow)

    actions.push({
      elementId,
      elementType: asString(element?.type),
      semanticRole: asString(element?.semanticRole),
      event,
      flow,
      flowExists: flow !== null && flows.has(flow),
    })
  }
  return actions
}
